import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Transaction, Category, Goal, Limit, Analytics, Settings } from '../types';

const dedupeTransactions = (list: Transaction[]): Transaction[] => {
  // 🔒 Защита от невалидного input
  if (!list) return [];
  
  // Убеждаемся что list это массив
  const safeList = Array.isArray(list) ? list : [];
  if (safeList.length === 0) return [];
  
  const seen = new Set<string>();
  const result: Transaction[] = [];
  
  // Используем индексный цикл вместо for...of для избежания ошибки iterator
  for (let i = 0; i < safeList.length; i++) {
    const tx = safeList[i];
    if (!tx) continue;
    
    const key =
      tx.plaid_transaction_id ||
      (tx.transaction_id ? `id-${tx.transaction_id}` : undefined) ||
      tx.web_id ||
      `${tx.title}-${tx.amount}-${tx.date}-${tx.payment_method_id || ''}`;
    if (!key) {
      result.push(tx);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(tx);
  }
  return result;
};

interface AppState {
  // Data
  transactions: Transaction[];
  categories: Category[];
  goals: Goal[];
  limits: Limit[];
  analytics: Analytics | null;
  settings: Settings;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  currentPage: 'home' | 'operations' | 'ai_chat' | 'goals' | 'settings' | 'notifications' | 'subscription' | 'plans' | 'security' | 'privacy' | 'terms' | 'request-password-reset' | 'reset-password';
  
  // Actions
  setTransactions: (transactions: Transaction[]) => void;
  addTransaction: (transaction: Transaction) => void;
  updateTransaction: (id: string, transaction: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  
  setCategories: (categories: Category[]) => void;
  addCategory: (category: Category) => void;
  updateCategory: (id: number, category: Partial<Category>) => void;
  deleteCategory: (id: number) => void;
  
  setGoals: (goals: Goal[]) => void;
  addGoal: (goal: Goal) => void;
  updateGoal: (id: string, goal: Partial<Goal>) => void;
  deleteGoal: (id: string) => void;
  
  setLimits: (limits: Limit[]) => void;
  updateLimit: (category: string, amount: number) => void;
  
  setAnalytics: (analytics: Analytics) => void;
  setSettings: (settings: Settings) => void;
  
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentPage: (page: 'home' | 'operations' | 'ai_chat' | 'goals' | 'settings' | 'notifications' | 'subscription' | 'plans' | 'security' | 'privacy' | 'terms' | 'request-password-reset' | 'reset-password') => void;
}

// Storage implementation that works for both mobile and web
const createStorage = () => {
  // Check if we're in a React Native environment
  const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
  
  if (isReactNative) {
    // For React Native, use AsyncStorage (will be imported dynamically)
    return {
      getItem: async (name: string) => {
        try {
          const AsyncStorage = require('@react-native-async-storage/async-storage').default;
          const value = await AsyncStorage.getItem(name);
          return value;
        } catch (error) {
          console.error('Error loading from AsyncStorage:', error);
          return null;
        }
      },
      setItem: async (name: string, value: string) => {
        try {
          const AsyncStorage = require('@react-native-async-storage/async-storage').default;
          await AsyncStorage.setItem(name, value);
        } catch (error) {
          console.error('Error saving to AsyncStorage:', error);
        }
      },
      removeItem: async (name: string) => {
        try {
          const AsyncStorage = require('@react-native-async-storage/async-storage').default;
          await AsyncStorage.removeItem(name);
        } catch (error) {
          console.error('Error removing from AsyncStorage:', error);
        }
      },
    };
  } else {
    // For web, use localStorage
    return {
      getItem: async (name: string) => {
        try {
          return localStorage.getItem(name);
        } catch (error) {
          console.error('Error loading from localStorage:', error);
          return null;
        }
      },
      setItem: async (name: string, value: string) => {
        try {
          localStorage.setItem(name, value);
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }
      },
      removeItem: async (name: string) => {
        try {
          localStorage.removeItem(name);
        } catch (error) {
          console.error('Error removing from localStorage:', error);
        }
      },
    };
  }
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial State
      transactions: [],
      categories: [],
      goals: [],
      limits: [],
      analytics: null,
      settings: {
        language: 'en',
        currency: 'USD',
      },
      isLoading: false,
      error: null,
      currentPage: 'home',
      
      // Actions
      setTransactions: (transactions) =>
        set(() => ({
          transactions: dedupeTransactions(transactions),
        })),
      
      addTransaction: (transaction) =>
        set((state) => ({
          transactions: dedupeTransactions([transaction, ...state.transactions]),
        })),
      
      updateTransaction: (id, transactionData) =>
        set((state) => ({
          transactions: dedupeTransactions(
            state.transactions.map((t) =>
              t.id === id ? { ...t, ...transactionData } : t
            )
          ),
        })),
      
      deleteTransaction: (id) =>
        set((state) => ({
          transactions: state.transactions.filter((t) => t.id !== id),
        })),
      
      setCategories: (categories) => set({ categories }),
      
      addCategory: (category) =>
        set((state) => ({
          categories: [...state.categories, category],
        })),
      
      updateCategory: (id, categoryData) =>
        set((state) => ({
          categories: state.categories.map((c) =>
            c.id === id ? { ...c, ...categoryData } : c
          ),
        })),
      
      deleteCategory: (id) =>
        set((state) => ({
          categories: state.categories.filter((c) => c.id !== id),
        })),
      
      setGoals: (goals) => set({ goals }),
      
      addGoal: (goal) =>
        set((state) => ({
          goals: [goal, ...state.goals],
        })),
      
      updateGoal: (id, goalData) =>
        set((state) => ({
          goals: state.goals.map((g) =>
            g.id === id ? { ...g, ...goalData } : g
          ),
        })),
      
      deleteGoal: (id) =>
        set((state) => ({
          goals: state.goals.filter((g) => g.id !== id),
        })),
      
      setLimits: (limits) => set({ limits }),
      
      updateLimit: (category, amount) =>
        set((state) => {
          const existingLimit = state.limits.find((l) => l.category_name === category);
          if (existingLimit) {
            return {
              limits: state.limits.map((l) =>
                l.category_name === category ? { ...l, limit_amount: amount } : l
              ),
            };
          } else {
            return {
              limits: [...state.limits, { id: 0, user_id: 0, category_name: category, limit_amount: amount, period_months: 1 }],
            };
          }
        }),
      
      setAnalytics: (analytics) => set({ analytics }),
      setSettings: (settings) => set((state) => ({ 
        settings: { 
          language: 'en', 
          currency: 'USD', 
          ...state.settings, 
          ...settings 
        } 
      })),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setCurrentPage: (currentPage) => set({ currentPage }),
    }),
    {
      name: 'alveon-app-storage',
      storage: createJSONStorage(() => createStorage()),
      partialize: (state) => ({
        // Only persist settings, not dynamic data
        settings: state.settings,
        // Categories управляются через PowerSync (SQLite), Zustand-кэш избыточен
      }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<AppState>),
        settings: {
          language: 'en',
          currency: 'USD',
          ...(persistedState as Partial<AppState>)?.settings,
        },
      }),
    }
  )
);

